<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { ARTICLE_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import FullEditor from 'dashboard/components/widgets/WootWriter/FullEditor.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  articleType: {
    type: String,
    default: 'public',
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  savingAction: {
    type: String,
    default: '',
  },
  portalSlug: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'save-draft', 'publish']);

const titleInputRef = ref(null);
const summaryInputRef = ref(null);
const title = ref('');
const summary = ref('');
const content = ref('');
const isExpanded = ref(false);

const articleLabel = computed(() =>
  props.articleType === 'internal' ? 'Internal article' : 'Public article'
);

const titlePlaceholder = computed(() =>
  props.articleType === 'internal'
    ? 'Untitled internal article'
    : 'Untitled public article'
);

const remainingSummaryChars = computed(() =>
  Math.max(0, 255 - summary.value.length)
);

const canSubmit = computed(() => {
  return Boolean(
    title.value.trim() || summary.value.trim() || content.value.trim()
  );
});
const hasUnsavedChanges = computed(() => canSubmit.value);

const editorMenuOptions = ARTICLE_EDITOR_MENU_OPTIONS;

const saveDraftLabel = computed(() => {
  if (!props.isSaving) return 'Save as draft';
  return props.savingAction === 'publish' ? 'Save as draft' : 'Saving draft...';
});

const publishLabel = computed(() => {
  if (!props.isSaving) return 'Publish';
  return props.savingAction === 'publish' ? 'Publishing...' : 'Publish';
});

const resizeTextarea = inputRef => {
  const element = inputRef?.value;
  if (!element) return;
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
};

const resetForm = () => {
  title.value = '';
  summary.value = '';
  content.value = '';
  isExpanded.value = false;
};

const lockScroll = locked => {
  const el = document?.documentElement;
  if (!el) return;
  el.style.overflow = locked ? 'hidden' : '';
};

const closeSheet = ({ force = false } = {}) => {
  if (props.isSaving) return;
  if (!force && hasUnsavedChanges.value) {
    const shouldDiscard = window.confirm(
      'Discard this article draft and close the editor?'
    );
    if (!shouldDiscard) return;
  }
  emit('close');
};

const onEscape = event => {
  const isModKey = event.metaKey || event.ctrlKey;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeSheet();
    return;
  }

  if (!canSubmit.value || props.isSaving) return;

  if (isModKey && event.key.toLowerCase() === 's') {
    event.preventDefault();
    onSaveDraft();
    return;
  }

  if (isModKey && event.key === 'Enter') {
    event.preventDefault();
    onPublish();
  }
};

const focusTitle = async () => {
  await nextTick();
  titleInputRef.value?.focus?.();
  resizeTextarea(titleInputRef);
  resizeTextarea(summaryInputRef);
};

const onSaveDraft = () => {
  if (!canSubmit.value) return;
  emit('save-draft', {
    type: props.articleType,
    title: title.value.trim(),
    summary: summary.value.trim(),
    content: content.value.trim(),
  });
};

const onPublish = () => {
  if (!canSubmit.value) return;
  emit('publish', {
    type: props.articleType,
    title: title.value.trim(),
    summary: summary.value.trim(),
    content: content.value.trim(),
  });
};

watch(
  () => props.show,
  async nextValue => {
    lockScroll(nextValue);
    if (nextValue) {
      window.addEventListener('keydown', onEscape);
      await focusTitle();
      return;
    }

    window.removeEventListener('keydown', onEscape);
    resetForm();
  },
  { immediate: true }
);

watch(title, async () => {
  await nextTick();
  resizeTextarea(titleInputRef);
});

watch(summary, async () => {
  await nextTick();
  resizeTextarea(summaryInputRef);
});

onBeforeUnmount(() => {
  lockScroll(false);
  window.removeEventListener('keydown', onEscape);
});
</script>

<template>
  <teleport to="body">
    <transition name="article-sheet-fade">
      <div
        v-if="show"
        class="article-sheet-overlay"
        @click.self="closeSheet"
      >
        <aside
          class="article-sheet fin-ai"
          :class="{ 'article-sheet--expanded': isExpanded }"
        >
          <div class="article-sheet__surface">
            <div class="article-sheet__header">
              <div class="article-sheet__header-title-wrap">
                <h1 class="heading o__size-1 u__one-truncated-line">
                  {{ articleLabel }}
                </h1>
              </div>

              <div class="article-sheet__header-actions">
                <button
                  class="btn o__secondary o__in-right-list"
                  type="button"
                  :disabled="isSaving"
                  @click="closeSheet"
                >
                  Cancel
                </button>

                <button
                  class="btn o__primary text-white article-sheet__cta"
                  type="button"
                  :disabled="!canSubmit || isSaving"
                  :class="{ o__disabled: !canSubmit || isSaving }"
                  @click="onSaveDraft"
                >
                  {{ saveDraftLabel }}
                </button>

                <button
                  class="btn o__primary-live article-sheet__cta article-sheet__cta--publish"
                  type="button"
                  :disabled="!canSubmit || isSaving"
                  :class="{ o__disabled: !canSubmit || isSaving }"
                  @click="onPublish"
                >
                  {{ publishLabel }}
                </button>

                <div class="article-sheet__header-divider" />

                <button
                  type="button"
                  class="btn o__secondary o__icon"
                  aria-label="Expand"
                  :disabled="isSaving"
                  @click="isExpanded = !isExpanded"
                >
                  <svg
                    class="interface-icon o__standard"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M6.48999 8.31L3.68999 11.11V9.41C3.68999 8.94 3.30999 8.56 2.83999 8.56C2.36999 8.56 1.98999 8.94 1.98999 9.41V14.01H6.58999C7.05999 14.01 7.43999 13.63 7.43999 13.16C7.43999 12.69 7.05999 12.31 6.58999 12.31H4.88999L7.68999 9.51L6.48999 8.31ZM9.40999 1.99C8.93999 1.99 8.55999 2.37 8.55999 2.84C8.55999 3.31 8.93999 3.69 9.40999 3.69H11.11L8.30999 6.49L9.50999 7.69L12.31 4.89V6.59C12.31 7.06 12.69 7.44 13.16 7.44C13.63 7.44 14.01 7.06 14.01 6.59V1.99H9.40999Z" />
                  </svg>
                </button>

                <button
                  type="button"
                  class="btn o__secondary o__icon"
                  aria-label="Close"
                  :disabled="isSaving"
                  @click="closeSheet"
                >
                  <svg
                    class="interface-icon o__standard"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M13.25 3.95L12.05 2.75L8 6.8L3.95 2.75L2.75 3.95L6.8 8L2.75 12.05L3.95 13.25L8 9.2L12.05 13.25L13.25 12.05L9.2 8L13.25 3.95Z" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="article-sheet__body">
              <div class="article-sheet__body-inner">
                <div class="article-sheet__editor-meta">
                  <span class="article-sheet__editor-chip">
                    {{ articleLabel }}
                  </span>
                  <span class="article-sheet__editor-shortcuts">
                    `Ctrl/Cmd+S` save draft, `Ctrl/Cmd+Enter` publish
                  </span>
                </div>
                <div class="article-sheet__editor">
                  <textarea
                    ref="titleInputRef"
                    v-model="title"
                    class="article-sheet__title"
                    :placeholder="titlePlaceholder"
                    rows="1"
                    @input="resizeTextarea(titleInputRef)"
                  />

                  <div class="article-sheet__summary-wrap">
                    <textarea
                      ref="summaryInputRef"
                      v-model="summary"
                      class="article-sheet__summary"
                      placeholder="Describe your article to help it get found"
                      rows="1"
                      maxlength="255"
                      @input="resizeTextarea(summaryInputRef)"
                    />
                    <span class="article-sheet__summary-remaining">
                      {{ remainingSummaryChars }}
                    </span>
                  </div>

                  <div class="article-sheet__composer">
                    <div class="article-sheet__editor-area">
                      <FullEditor
                        v-model="content"
                        class="article-sheet__full-editor"
                        placeholder="Start writing..."
                        :enabled-menu-options="editorMenuOptions"
                        :portal-slug="portalSlug"
                        :autofocus="false"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.article-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(15, 23, 42, 0.2);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: flex-end;
}

.article-sheet {
  width: min(1140px, 88vw);
  height: 100vh;
  background: #ffffff;
  box-shadow: -28px 0 60px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(229, 231, 235, 0.9);
}

.article-sheet--expanded {
  width: min(1340px, 95vw);
}

.article-sheet__surface {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--fin-base, #ffffff);
}

.article-sheet .interface-icon {
  display: inline-block;
  flex-shrink: 0;
  fill: currentColor;
}

.article-sheet .u__one-truncated-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.article-sheet .btn.o__in-right-list {
  border-radius: 9999px;
  min-height: 34px;
  padding-inline: 14px;
}

.article-sheet .btn.o__primary,
.article-sheet .btn.o__primary-live {
  background: #111827;
  color: #ffffff;
  border-color: #111827;
}

.article-sheet .btn.o__secondary {
  border-color: #e5e7eb;
  background: #ffffff;
  color: #111827;
}

.article-sheet .btn.o__secondary:hover {
  background: #f8fafc;
  border-color: #d1d5db;
}

.article-sheet .btn.o__primary:hover,
.article-sheet .btn.o__primary-live:hover {
  background: #1f2937;
  border-color: #1f2937;
}

.article-sheet .btn.o__disabled,
.article-sheet .btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.article-sheet__header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--fin-neutral-border, #e9eae6);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
}

.article-sheet__header-title-wrap {
  min-width: 0;
  flex: 1 1 auto;
}

.article-sheet__header-title-wrap .heading {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.article-sheet__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.article-sheet__header-divider {
  width: 1px;
  height: 20px;
  background: var(--fin-neutral-border, #e9eae6);
  margin: 0 4px;
}

.article-sheet__cta {
  border-radius: 9999px;
  min-height: 34px;
  padding-inline: 16px;
  font-weight: 600;
}

.article-sheet__cta--publish {
  background: #111827;
  color: #ffffff;
  border: 1px solid #111827;
}

.article-sheet__cta--publish:hover {
  background: #1f2937;
}

.article-sheet__body {
  flex: 1;
  overflow: auto;
}

.article-sheet__body-inner {
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
  padding: 32px 24px 56px;
}

.article-sheet__editor-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.article-sheet__editor-chip {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid #e6ebf1;
  border-radius: 9999px;
  background: #f8fafc;
  color: #475467;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.article-sheet__editor-shortcuts {
  color: #8a94a6;
  font-size: 12px;
  line-height: 1.4;
}

.article-sheet__editor {
  width: 100%;
}

.article-sheet__title,
.article-sheet__summary {
  width: 100%;
  border: 0;
  outline: none;
  resize: none;
  color: #111827;
  background: transparent;
}

.article-sheet__title {
  min-height: 52px;
  font-size: 34px;
  line-height: 1.12;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-bottom: 14px;
}

.article-sheet__title::placeholder {
  color: #c0c6d0;
}

.article-sheet__summary-wrap {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf1f5;
}

.article-sheet__summary {
  flex: 1 1 auto;
  font-size: 14px;
  line-height: 1.6;
  font-weight: 500;
  min-height: 32px;
}

.article-sheet__summary::placeholder {
  color: #9aa4b2;
}

.article-sheet__summary-remaining {
  font-size: 12px;
  line-height: 20px;
  color: #6b7280;
  flex-shrink: 0;
  padding-top: 4px;
}

.article-sheet__composer {
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #fcfcfd 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.article-sheet__editor-area {
  min-height: 360px;
  padding: 0;
}

.article-sheet__editor-area :deep(.editor-root) {
  min-height: 360px;
}

.article-sheet__editor-area :deep(.ProseMirror .empty-node::before) {
  color: #9aa4b2;
  font-size: 15px;
}

.article-sheet__editor-area :deep(.ProseMirror-menubar-wrapper) {
  display: flex;
  flex-direction: column;
}

.article-sheet__editor-area :deep(.ProseMirror-menubar) {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 5;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  min-height: 52px;
  padding: 10px 12px;
  margin: 0;
  border-bottom: 1px solid #edf1f5;
  background: rgba(251, 252, 253, 0.96);
  backdrop-filter: blur(12px);
}

.article-sheet__editor-area :deep(.ProseMirror-menuitem) {
  margin-right: 0;
}

.article-sheet__editor-area :deep(.ProseMirror-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  border-radius: 10px;
  color: #4b5563;
  transition: background-color 0.16s ease, color 0.16s ease;
}

.article-sheet__editor-area :deep(.ProseMirror-icon svg) {
  width: 18px !important;
  height: 18px !important;
}

.article-sheet__editor-area :deep(.ProseMirror-icon:hover) {
  background: #f3f6fa;
  color: #111827;
}

.article-sheet__editor-area :deep(.ProseMirror-menu-active) {
  background: #eef2f7;
  color: #111827;
  border-radius: 10px;
}

.article-sheet__editor-area :deep(.ProseMirror-menubar-wrapper > .ProseMirror) {
  padding: 20px 24px 24px;
  word-break: break-word;
}

.article-sheet__editor-area :deep(.ProseMirror-woot-style) {
  min-height: 340px;
  max-height: none;
  overflow: visible;
  color: #111827;
  font-size: 14px;
  line-height: 1.7;
}

.article-sheet__editor-area :deep(.editor-root.has-selection .ProseMirror-menubar) {
  position: sticky !important;
  top: 0 !important;
  left: auto !important;
  width: auto !important;
  box-shadow: none;
}

@media (max-width: 1024px) {
  .article-sheet {
    width: min(100vw, 100vw);
  }

  .article-sheet--expanded {
    width: min(100vw, 100vw);
  }

  .article-sheet__header {
    flex-wrap: wrap;
  }

  .article-sheet__header-actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .article-sheet__body-inner {
    padding: 32px 24px 48px;
  }

  .article-sheet__editor-meta {
    align-items: flex-start;
    flex-direction: column;
  }

  .article-sheet__title {
    font-size: 34px;
  }
}

.article-sheet-fade-enter-active,
.article-sheet-fade-leave-active {
  transition: opacity 0.18s ease;
}

.article-sheet-fade-enter-from,
.article-sheet-fade-leave-to {
  opacity: 0;
}
</style>
